/** Schedules B (leasing factor rates) and C (terminal costs & bundle packages) on a partner type's draft. */
import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { btnPrimary, btnSmall, inputCls, money } from '../finance/financeUi';

const cell = 'h-8 min-w-0 rounded border bg-background px-2 text-sm disabled:opacity-70';

function Rows({ rows, setRows, cols, testid, locked }) {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-1" data-testid={testid}>
      {rows.length > 0 && (
        <div className="flex gap-2 text-xs font-medium text-muted-foreground" data-testid={`${testid}-headers`}>
          {cols.map(([k, ph, , w]) => <span key={k} className={`${w} ${w === 'flex-1' ? '' : 'shrink-0'}`}>{ph}</span>)}
          {!locked && <span className="w-6 shrink-0" />}
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2" data-testid={`${testid}-row-${i}`}>
          {cols.map(([k, ph, type, w]) => <input key={k} className={`${cell} ${w} ${w === 'flex-1' ? '' : 'shrink-0'}`} type={type} placeholder={ph} disabled={locked} value={r[k] ?? ''} onChange={(e) => set(i, k, e.target.value)} data-testid={`${testid}-${i}-${k}`} />)}
          {!locked && <button type="button" aria-label="Remove" className="rounded p-1 hover:bg-muted" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`${testid}-${i}-remove`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      {!locked && <button type="button" className={btnSmall} onClick={() => setRows([...rows, {}])} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" />Add row</button>}
    </div>
  );
}

export function ScheduleBCEditor({ master, locked, busy, onSave }) {
  const [b, setB] = useState(master.schedule_b || { rows: [], notes: '' });
  const [c, setC] = useState(master.schedule_c || { items: [], notes: '' });
  useEffect(() => { setB(master.schedule_b || { rows: [], notes: '' }); setC(master.schedule_c || { items: [], notes: '' }); }, [master]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <p className="font-medium">Schedule B — leasing factor rates</p>
        <Rows rows={b.rows} setRows={(rows) => setB({ ...b, rows })} cols={[['term_months', 'Term (months)', 'number', 'w-32'], ['factor', 'Factor (e.g. 0.0455)', 'number', 'w-40'], ['note', 'Note', 'text', 'flex-1']]} testid="schedule-b" locked={locked} />
        <textarea className={`${inputCls} h-20 py-2`} placeholder="Schedule B notes" disabled={locked} value={b.notes || ''} onChange={(e) => setB({ ...b, notes: e.target.value })} data-testid="schedule-b-notes" />
      </div>
      <div className="space-y-2">
        <p className="font-medium">Schedule C — terminal costs & bundle packages</p>
        <Rows rows={c.items} setRows={(items) => setC({ ...c, items })} cols={[['name', 'Terminal / bundle', 'text', 'w-44'], ['cost', 'Cost $', 'number', 'w-24'], ['includes', 'What it includes', 'text', 'flex-1']]} testid="schedule-c" locked={locked} />
        <textarea className={`${inputCls} h-20 py-2`} placeholder="Schedule C notes" disabled={locked} value={c.notes || ''} onChange={(e) => setC({ ...c, notes: e.target.value })} data-testid="schedule-c-notes" />
        {locked && <p className="text-xs text-muted-foreground">{c.items.length} items · total list cost {money(c.items.reduce((s, i) => s + (Number(i.cost) || 0), 0))}</p>}
      </div>
      {!locked && <button type="button" className={`${btnPrimary} w-fit`} disabled={busy} onClick={() => onSave({ schedule_b: b, schedule_c: c })} data-testid="schedule-bc-save-btn">Save Schedules B & C</button>}
    </div>
  );
}
